import { useMemo, useState, useCallback } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import * as state from '@/state';
import { useCreateWorkflowStageMutation, StageType } from '@/__generated__/graphql';
import { useSnackBarMessage, sortWorkflowStages } from '@/components/ZonesManagementRevamped/utils';
import { GenericDialog } from '@/components/shared/GenericDialog';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { labelStyles } from './styles';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { formatEnumForDisplay } from '@/utils';
import { FINAL_WORKFLOW_STAGE } from '@/constants';

type AddStageProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

export const AddStage = ({ isOpen, setIsOpen }: AddStageProps) => {
  const structureId = useAtomValue(state.selectedStructureId);
  const [workflowStages, setWorkflowStages] = useAtom(state.workflowStages);
  const [newStage, setNewStage] = useState({
    stageName: '',
    position: '',
    type: StageType.Review,
  });
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const stagePositionsMap: Record<string, number> = useMemo(() => {
    return workflowStages
      ?.filter((stage) => stage.name !== FINAL_WORKFLOW_STAGE)
      ?.reduce(
        (accumulator, stage) =>
          stage?.name && stage?.orderInWorkflow
            ? {
                ...accumulator,
                [`After ${stage.name}`]: stage.orderInWorkflow + 1,
              }
            : accumulator,
        { Start: 1 }
      );
  }, [workflowStages]);

  const [addNewStage, { loading }] = useCreateWorkflowStageMutation();

  const handleAddStage = useCallback(() => {
    if (structureId && newStage.stageName && newStage.position) {
      addNewStage({
        variables: {
          input: {
            structureId: structureId,
            workflowStageName: newStage.stageName,
            orderInWorkflow: stagePositionsMap[newStage.position],
            stageType: newStage.type,
          },
        },
        onCompleted: (response) => {
          if (response?.createWorkflowStage?.workflowStages) {
            const sortedStages = sortWorkflowStages(
              response?.createWorkflowStage?.workflowStages?.filter(
                (stage): stage is NonNullable<typeof stage> => stage !== null
              )
            );
            setWorkflowStages(sortedStages);
            showSnackBar('The new stage has been added successfully', 'success');
            setIsOpen(false);
          }
        },
        onError: (errorResponse) => {
          console.error(errorResponse);
          showSnackBar(`${errorResponse.message}`, 'error');
          setIsOpen(false);
        },
      });
    }
  }, [
    structureId,
    newStage.stageName,
    newStage.position,
    newStage.type,
    addNewStage,
    stagePositionsMap,
    setWorkflowStages,
    showSnackBar,
    setIsOpen,
  ]);

  const isDisabled = useMemo(() => {
    return loading || Object.values(newStage).some((value) => value === '');
  }, [loading, newStage]);

  return (
    <GenericDialog
      open={isOpen}
      onClose={() => setIsOpen(false)}
      showCloseIcon
      title="Add Stage"
      content={
        <Stack direction="column" spacing={3}>
          <TextField
            label="New Stage Name"
            variant="standard"
            fullWidth
            placeholder="Stage Name"
            value={newStage.stageName}
            onChange={(event) => setNewStage({ ...newStage, stageName: event.target.value })}
            sx={labelStyles}
          />
          <FormControl variant="standard" sx={labelStyles}>
            <InputLabel>Insert Position</InputLabel>
            <Select
              value={newStage.position}
              onChange={(event) => setNewStage({ ...newStage, position: event.target.value })}
              label="Insert Position"
            >
              {Object.keys(stagePositionsMap)?.map((position) => (
                <MenuItem key={position} value={position}>
                  {position}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="standard" sx={labelStyles}>
            <InputLabel>Stage Type</InputLabel>
            <Select
              value={newStage.type}
              onChange={(event) =>
                setNewStage({ ...newStage, type: event.target.value as StageType })
              }
              label="Stage Type"
            >
              {Object.values(StageType)?.map((type) => (
                <MenuItem key={type} value={type}>
                  {formatEnumForDisplay(type)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      }
      actions={
        <>
          <Button onClick={() => setIsOpen(false)} variant="text" sx={textButtonStyle}>
            Cancel
          </Button>
          <Button
            onClick={() => handleAddStage()}
            sx={containedButtonStyle}
            disabled={isDisabled}
            variant="contained"
          >
            {loading ? 'Adding' : 'Confirm'}
            {loading && <CircularProgress color="info" size={15} sx={{ ml: 1 }} />}
          </Button>
        </>
      }
      actionsStyles={{ mr: 3, pb: 3 }}
    />
  );
};
